import { useState, type FormEvent } from "react";
import { todayISO } from "../lib/day.ts";
import type { Entry, EntryInput, Mood } from "../lib/types.ts";
import { EnergySlider } from "./EnergySlider.tsx";
import { MoodPicker } from "./MoodPicker.tsx";

type Props = {
  moods: Mood[];
  /** Si viene, el formulario edita ese registro en vez de crear uno nuevo. */
  entry?: Entry;
  /** Permite elegir la fecha (para rellenar dias pasados). */
  allowDate?: boolean;
  submitLabel?: string;
  onSubmit: (input: EntryInput) => Promise<void>;
  onCancel?: () => void;
};

export function EntryForm({
  moods,
  entry,
  allowDate = false,
  submitLabel = "Guardar registro",
  onSubmit,
  onCancel,
}: Props) {
  const [energy, setEnergy] = useState(entry?.energy ?? 50);
  const [mood, setMood] = useState<string | null>(entry?.mood.slug ?? null);
  const [note, setNote] = useState(entry?.note ?? "");
  const [date, setDate] = useState(entry?.date ?? todayISO());
  const [showNote, setShowNote] = useState(Boolean(entry?.note));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!mood) {
      setError("Elige un estado de ánimo.");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        energy,
        mood,
        note: note.trim() ? note.trim() : null,
        ...(allowDate ? { date } : {}),
      });
      if (!entry) {
        // Tras crear, deja el formulario listo para otro registro del dia.
        setNote("");
        setShowNote(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      <EnergySlider value={energy} onChange={setEnergy} />

      <MoodPicker moods={moods} value={mood} onChange={setMood} />

      {allowDate && (
        <div className="field">
          <label className="field__label" htmlFor="date">Fecha</label>
          <input
            id="date"
            className="input"
            type="date"
            value={date}
            max={todayISO()}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      )}

      {showNote ? (
        <div className="field">
          <label className="field__label" htmlFor="note">
            Comentario <span className="muted">(opcional)</span>
          </label>
          <textarea
            id="note"
            className="textarea"
            maxLength={2000}
            placeholder="¿Qué ha pasado hoy?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      ) : (
        <button
          type="button"
          className="btn btn--quiet"
          style={{ alignSelf: "flex-start" }}
          onClick={() => setShowNote(true)}
        >
          + Añadir un comentario
        </button>
      )}

      <div className="row">
        <button className="btn" type="submit" disabled={busy || !mood}>
          {busy ? "Guardando..." : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}
